import { PostHogProvider } from "posthog-js/react";

import {
  COMMAND_LINK_API_TOKEN_QUERY_PARAM_KEY,
  COMMAND_LINK_TOKEN_QUERY_PARAM_KEY,
} from "../../constants";

import type {
  CapturedNetworkRequest,
  CaptureResult,
  PostHogConfig,
} from "posthog-js";
import type { ReactNode } from "react";

interface PostHogProviderWrapperProps {
  children: ReactNode;
}

// The landing URL names the signed token `t`; the command-link API names the same
// token `token` on its preview call. Both spellings are stripped.
const TOKEN_QUERY_PARAM_KEYS = [
  COMMAND_LINK_TOKEN_QUERY_PARAM_KEY,
  COMMAND_LINK_API_TOKEN_QUERY_PARAM_KEY,
];

/**
 * Command-link landings are reached with a signed token in the query string, and
 * posthog-js stamps the full URL onto every event — including the automatic
 * $pageview it sends from init, which fires before any landing component can
 * strip the token. Those tokens carry real authority (submitting a quote,
 * activating an account), so they are removed from every URL property here, at
 * the one place all events pass through
 * (docs/engineering/design/carrier_submit_quote_command_link_design.md §10).
 */
function removeCommandLinkToken(url: string): string {
  try {
    const parsed = new URL(url);
    const carriedKeys = TOKEN_QUERY_PARAM_KEYS.filter((key) =>
      parsed.searchParams.has(key),
    );
    if (carriedKeys.length === 0) {
      return url;
    }
    carriedKeys.forEach((key) => parsed.searchParams.delete(key));
    return parsed.toString();
  } catch {
    // Not an absolute URL, so it carries no query string we own.
    return url;
  }
}

const URL_PROPERTIES_TO_SANITIZE = [
  "$current_url",
  "$initial_current_url",
  "$referrer",
  "$initial_referrer",
  "$pathname",
];

export function PostHogProviderWrapper(props: PostHogProviderWrapperProps) {
  const { children } = props;
  const postHogKey = import.meta.env.REACT_APP_POSTHOG_KEY;

  const postHogOptions: Partial<PostHogConfig> = {
    api_host: "https://us.i.posthog.com",
    /**
     * posthog-js defaults to the legacy `true`, which sends a $pageview only on
     * a full page load. The app is a single-page app, so in-app navigation to a
     * route (e.g. the dashboard to a shipment) would otherwise leave no
     * $pageview for that route and be invisible to funnels and path analyses
     * that start there. `history_change` keeps the initial $pageview and also
     * captures one per pushState / replaceState / popstate that changes the
     * pathname; same-path updates, such as query-string rewrites, send nothing.
     */
    capture_pageview: "history_change",
    before_send: (event: CaptureResult | null): CaptureResult | null => {
      if (event == null) {
        return event;
      }
      const properties = { ...event.properties };
      URL_PROPERTIES_TO_SANITIZE.forEach((key) => {
        if (typeof properties[key] === "string") {
          properties[key] = removeCommandLinkToken(properties[key]);
        }
      });
      return { ...event, properties };
    },
    session_recording: {
      /**
       * Event properties are not the only place a URL reaches PostHog: a session
       * recording captures the requests the page made, and the preview call carries
       * the token in its query string. `before_send` above does not see those, so
       * they are masked here — `name` is the request URL.
       */
      maskCapturedNetworkRequestFn: (
        request: CapturedNetworkRequest,
      ): CapturedNetworkRequest => ({
        ...request,
        name: removeCommandLinkToken(request.name),
      }),
    },
  };

  const shouldInitializePostHog = postHogKey && postHogKey !== "no-op";

  if (!shouldInitializePostHog) {
    return <div>{children}</div>;
  }

  return (
    <PostHogProvider apiKey={postHogKey} options={postHogOptions}>
      {children}
    </PostHogProvider>
  );
}
